import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Mail, Plus, Save, Send, Trash2, UserPlus } from 'lucide-react'
import {
  EMAIL_SENDING_DOMAIN,
  MAX_NOTIFICATION_RECIPIENTS,
  RECOMMENDATION_SEVERITY_LABELS,
  RECOMMENDATION_SEVERITY_ORDER,
  RECOMMENDATION_TYPE_LABELS,
  type RecommendationSeverity,
  type RecommendationType,
  type TenantNotificationSettings,
} from '@roi365/shared'
import { api } from '@/lib/api-client'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'

interface TeamMember {
  id: string
  email: string
  name: string
  role: string
  status: string
}

type Criteria = Pick<
  TenantNotificationSettings,
  'recommendationsEnabled' | 'recipients' | 'recommendationTypes' | 'minSeverity' | 'minMonthlySaving' | 'notifyOnRecalculate'
>

const inputClass = 'w-full rounded-md border px-3 py-2 text-sm'
const inputStyle = { borderColor: 'var(--color-border)' }
const labelStyle = { color: 'var(--color-text-secondary)' }
const helpStyle = { color: 'var(--color-text-tertiary)' }

function Toggle({ enabled, onChange, disabled }: { enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => !disabled && onChange(!enabled)}
      className="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200"
      style={{
        backgroundColor: enabled ? 'var(--color-brand-secondary)' : 'var(--color-gray-300)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        className="pointer-events-none inline-block h-5 w-5 rounded-full shadow transition-transform duration-200"
        style={{ backgroundColor: 'white', transform: enabled ? 'translateX(1.25rem)' : 'translateX(0)' }}
      />
    </button>
  )
}

// ─── Remitente (solo Super Admin edita) ───────────────────────

function SenderCard({ settings }: { settings: TenantNotificationSettings }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isSuperAdmin = user?.role === 'super_admin'
  const [fromName, setFromName] = useState(settings.fromName)
  const [localPart, setLocalPart] = useState(settings.fromAddress.split('@')[0])

  useEffect(() => {
    setFromName(settings.fromName)
    setLocalPart(settings.fromAddress.split('@')[0])
  }, [settings.fromName, settings.fromAddress])

  const save = useMutation({
    mutationFn: () =>
      api.put<TenantNotificationSettings>('/settings/notifications/from', {
        fromName,
        fromAddress: `${localPart.trim()}@${EMAIL_SENDING_DOMAIN}`,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'notifications'], data)
      toast.success('Remitente guardado')
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const dirty = fromName !== settings.fromName || `${localPart}@${EMAIL_SENDING_DOMAIN}` !== settings.fromAddress

  return (
    <Card>
      <CardHeader>
        <CardTitle>Remitente</CardTitle>
        {!isSuperAdmin && (
          <span className="text-xs" style={helpStyle}>Solo el Super Admin puede cambiarlo</span>
        )}
      </CardHeader>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium" style={labelStyle}>Nombre</label>
          <input
            className={inputClass}
            style={inputStyle}
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            disabled={!isSuperAdmin}
            maxLength={60}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium" style={labelStyle}>Dirección</label>
          <div className="flex items-center gap-1">
            <input
              className={inputClass}
              style={inputStyle}
              value={localPart}
              onChange={(e) => setLocalPart(e.target.value)}
              disabled={!isSuperAdmin}
              maxLength={64}
            />
            <span className="whitespace-nowrap text-sm" style={helpStyle}>@{EMAIL_SENDING_DOMAIN}</span>
          </div>
        </div>
      </div>
      <p className="mt-2 text-xs" style={helpStyle}>
        Los correos de esta organización (avisos e invitaciones) salen como {fromName || 'ROI365'} &lt;{localPart || 'roi365'}@{EMAIL_SENDING_DOMAIN}&gt;.
      </p>
      {isSuperAdmin && (
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty || !localPart.trim() || !fromName.trim()}>
            <Save className="h-3.5 w-3.5" /> Guardar remitente
          </Button>
        </div>
      )}
    </Card>
  )
}

// ─── Destinatarios ────────────────────────────────────────────

function RecipientsEditor({
  recipients,
  onChange,
}: {
  recipients: string[]
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const { data: team } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/team'),
    enabled: pickerOpen,
  })

  const full = recipients.length >= MAX_NOTIFICATION_RECIPIENTS

  const add = (email: string) => {
    const e = email.trim().toLowerCase()
    if (!e) return
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error('Email inválido')
      return
    }
    if (recipients.includes(e)) {
      toast.error('Ese destinatario ya está en la lista')
      return
    }
    if (full) {
      toast.error(`Máximo ${MAX_NOTIFICATION_RECIPIENTS} destinatarios`)
      return
    }
    onChange([...recipients, e])
    setDraft('')
  }

  const candidates = (team ?? []).filter((m) => m.status !== 'disabled' && !recipients.includes(m.email.toLowerCase()))

  return (
    <div>
      <label className="mb-1 block text-xs font-medium" style={labelStyle}>
        Destinatarios ({recipients.length}/{MAX_NOTIFICATION_RECIPIENTS})
      </label>
      {recipients.length === 0 ? (
        <p className="mb-2 text-sm" style={helpStyle}>Sin destinatarios: no se enviará ningún aviso.</p>
      ) : (
        <ul className="mb-2 flex flex-wrap gap-2">
          {recipients.map((r) => (
            <li
              key={r}
              className="flex items-center gap-1 rounded-full border px-3 py-1 text-xs"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              {r}
              <button
                type="button"
                aria-label={`Quitar ${r}`}
                onClick={() => onChange(recipients.filter((x) => x !== r))}
                className="rounded p-0.5 hover:bg-gray-100"
              >
                <Trash2 className="h-3 w-3" style={helpStyle} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="email"
          className={`${inputClass} max-w-xs`}
          style={inputStyle}
          placeholder="correo@empresa.com"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add(draft)
            }
          }}
          disabled={full}
        />
        <Button size="sm" variant="secondary" onClick={() => add(draft)} disabled={full || !draft.trim()}>
          <Plus className="h-3.5 w-3.5" /> Añadir
        </Button>
        <div className="relative">
          <Button size="sm" variant="secondary" onClick={() => setPickerOpen((v) => !v)} disabled={full}>
            <UserPlus className="h-3.5 w-3.5" /> Del equipo
          </Button>
          {pickerOpen && (
            <ul
              className="absolute z-20 mt-1 max-h-56 w-72 overflow-y-auto rounded-md border shadow-lg"
              style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
            >
              {candidates.length === 0 ? (
                <li className="px-3 py-2 text-sm" style={helpStyle}>
                  {team ? 'No quedan miembros por añadir' : 'Cargando…'}
                </li>
              ) : (
                candidates.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                      onClick={() => {
                        add(m.email)
                        setPickerOpen(false)
                      }}
                    >
                      <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{m.name}</span>
                      <span className="ml-2 text-xs" style={helpStyle}>{m.email}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Avisos de recomendaciones ────────────────────────────────

function toCriteria(s: TenantNotificationSettings): Criteria {
  return {
    recommendationsEnabled: s.recommendationsEnabled,
    recipients: s.recipients,
    recommendationTypes: s.recommendationTypes,
    minSeverity: s.minSeverity,
    minMonthlySaving: s.minMonthlySaving,
    notifyOnRecalculate: s.notifyOnRecalculate,
  }
}

function RecommendationAlertsCard({ settings }: { settings: TenantNotificationSettings }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<Criteria>(() => toCriteria(settings))
  useEffect(() => setForm(toCriteria(settings)), [settings])

  const save = useMutation({
    mutationFn: () => api.put<TenantNotificationSettings>('/settings/notifications', form),
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'notifications'], data)
      toast.success('Configuración de avisos guardada')
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const dirty = JSON.stringify(form) !== JSON.stringify(toCriteria(settings))
  const allTypes = Object.keys(RECOMMENDATION_TYPE_LABELS) as RecommendationType[]
  const toggleType = (t: RecommendationType) =>
    setForm((f) => ({
      ...f,
      recommendationTypes: f.recommendationTypes.includes(t)
        ? f.recommendationTypes.filter((x) => x !== t)
        : [...f.recommendationTypes, t],
    }))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Avisos de recomendaciones</CardTitle>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={helpStyle}>{form.recommendationsEnabled ? 'Activado' : 'Desactivado'}</span>
          <Toggle enabled={form.recommendationsEnabled} onChange={(v) => setForm((f) => ({ ...f, recommendationsEnabled: v }))} />
        </div>
      </CardHeader>
      <p className="mb-4 text-sm" style={labelStyle}>
        Tras cada sincronización que genere recomendaciones nuevas que cumplan estos criterios se envía un único resumen
        por correo con tipo, título, ahorro estimado y enlace a Recomendaciones.
        {settings.lastSentAt && <> Último aviso: {new Date(settings.lastSentAt.replace(' ', 'T') + 'Z').toLocaleString('es-ES')}.</>}
      </p>

      <div className="space-y-5">
        <RecipientsEditor recipients={form.recipients} onChange={(recipients) => setForm((f) => ({ ...f, recipients }))} />

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="block text-xs font-medium" style={labelStyle}>Tipos de recomendación</label>
            <div className="flex gap-3 text-xs">
              <button type="button" className="underline" style={helpStyle} onClick={() => setForm((f) => ({ ...f, recommendationTypes: allTypes }))}>Todos</button>
              <button type="button" className="underline" style={helpStyle} onClick={() => setForm((f) => ({ ...f, recommendationTypes: [] }))}>Ninguno</button>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {allTypes.map((t) => (
              <label key={t} className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                <input type="checkbox" checked={form.recommendationTypes.includes(t)} onChange={() => toggleType(t)} />
                {RECOMMENDATION_TYPE_LABELS[t]}
              </label>
            ))}
          </div>
          {form.recommendationTypes.length === 0 && (
            <p className="mt-1 text-xs" style={helpStyle}>Sin tipos seleccionados no se enviará ningún aviso.</p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium" style={labelStyle}>Severidad mínima</label>
            <select
              className={inputClass}
              style={inputStyle}
              value={form.minSeverity}
              onChange={(e) => setForm((f) => ({ ...f, minSeverity: e.target.value as RecommendationSeverity }))}
            >
              {RECOMMENDATION_SEVERITY_ORDER.map((s) => (
                <option key={s} value={s}>{RECOMMENDATION_SEVERITY_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium" style={labelStyle}>Ahorro mensual mínimo por recomendación</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                step={1}
                className={`${inputClass} w-32 tabular-nums`}
                style={inputStyle}
                value={form.minMonthlySaving}
                onChange={(e) => setForm((f) => ({ ...f, minMonthlySaving: Math.max(0, Number(e.target.value) || 0) }))}
              />
              <span className="text-xs" style={helpStyle}>€ (0 = sin filtro)</span>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm" style={{ color: 'var(--color-text-primary)' }}>
          <Toggle enabled={form.notifyOnRecalculate} onChange={(v) => setForm((f) => ({ ...f, notifyOnRecalculate: v }))} />
          Avisar también al pulsar «Recalcular recomendaciones» manualmente
        </label>
      </div>

      <div className="mt-6 flex justify-end">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
          <Save className="h-3.5 w-3.5" /> Guardar avisos
        </Button>
      </div>
    </Card>
  )
}

// ─── Correo de prueba ─────────────────────────────────────────

function TestEmailCard() {
  const { user } = useAuth()
  const test = useMutation({
    mutationFn: () => api.post<{ delivered: boolean; to: string }>('/settings/notifications/test'),
    onSuccess: (data) =>
      data.delivered
        ? toast.success(`Correo de prueba enviado a ${data.to}`)
        : toast.error('No se pudo enviar el correo de prueba. Revisa la configuración del servicio de envío.'),
    onError: (err) => toast.error((err as Error).message),
  })
  return (
    <Card>
      <CardHeader><CardTitle>Correo de prueba</CardTitle></CardHeader>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm" style={labelStyle}>
          Envía un correo a tu buzón ({user?.email}) con el remitente configurado para comprobar la entrega.
        </p>
        <Button size="sm" variant="secondary" onClick={() => test.mutate()} loading={test.isPending}>
          <Send className="h-3.5 w-3.5" /> Enviar correo de prueba
        </Button>
      </div>
    </Card>
  )
}

export function SettingsEmailPage() {
  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => api.get<TenantNotificationSettings>('/settings/notifications'),
  })

  if (isLoading || !settings) {
    return (
      <div className="flex items-center gap-2 text-sm" style={helpStyle}>
        <Mail className="h-4 w-4" /> Cargando configuración de correo…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <SenderCard settings={settings} />
      <RecommendationAlertsCard settings={settings} />
      <TestEmailCard />
    </div>
  )
}
